import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import { AppDispatch, RootState } from '../../store';
import { 
  fetchDashboardStats, 
  fetchRecentAdherents, 
  fetchDistributions,
  fetchAdherentsForMap
} from '../../store/slices/dashboardSlice';
import { AdherentMapComponent } from '../../components/map';

export default function DashboardPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { 
    stats, 
    recentAdherents, 
    distributions,
    mapAdherents,
    loadingStats, 
    loadingAdherents, 
    loadingDistributions,
    loadingMapAdherents, 
    error 
  } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    // Fetch dashboard data when component mounts
    dispatch(fetchDashboardStats());
    dispatch(fetchRecentAdherents(5));
    dispatch(fetchDistributions());
    dispatch(fetchAdherentsForMap());
  }, [dispatch]);

  // Safely get NAF code data
  const topNafCode = distributions?.nafCodes?.length ? distributions.nafCodes[0] : null;
  const nafLabel = topNafCode?.label 
    ? topNafCode.label.length > 20 
      ? topNafCode.label.substring(0, 20) + '...' 
      : topNafCode.label
    : '';

  // Generate stats cards based on real data with null safety
  const statsCards = [
    {
      name: 'Total Adhérents',
      value: stats?.stats?.totalAdherents?.toString() || '0',
      icon: UserGroupIcon,
      change: stats?.stats?.percentChange !== undefined 
        ? `+${stats?.stats?.percentChange}% en 1 mois` 
        : 'Aucune variation',
      changeType: stats?.stats?.percentChange !== undefined
        ? (stats.stats.percentChange > 0 
          ? 'increase' 
          : stats.stats.percentChange < 0 
            ? 'decrease' 
            : 'neutral')
        : 'neutral',
    },
    {
      name: 'Adhérents ce mois-ci',
      value: stats?.stats?.newAdherentsLastMonth?.toString() || '0',
      icon: ArrowTrendingUpIcon,
      change: stats?.stats?.newAdherentsLastWeek 
        ? `+${stats?.stats?.newAdherentsLastWeek} sur les 7 derniers jours` 
        : '',
      changeType: 'increase',
    },
    {
      name: 'NAF le plus fréquent',
      value: topNafCode?.count?.toString() || '0',
      icon: ChartBarIcon,
      change: nafLabel,
      changeType: 'neutral',
    },
  ];

  // Loading state
  if (loadingStats && !stats) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Bonjour, {user?.firstName} 👋
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Voici un aperçu de vos activités
          </p>
        </div>
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            <h3 className="ml-3 text-sm font-medium text-red-800">
              Impossible de charger les données du tableau de bord
            </h3>
          </div>
          <div className="mt-2 text-sm text-red-700">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          Bonjour, {user?.firstName} 👋
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Voici un aperçu de vos activités chez {stats?.company?.name || 'votre entreprise'}
        </p>
      </div>

      <div>
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {statsCards.map((item) => (
            <div
              key={item.name}
              className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow sm:px-6 sm:pt-6"
            >
              <dt>
                <div className="absolute rounded-md bg-primary-500 p-3">
                  <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-gray-500">
                  {item.name}
                </p>
              </dt>
              <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
                <p className="text-2xl font-semibold text-gray-900">
                  {item.value}
                </p>
                <p
                  className={`ml-2 flex items-baseline text-sm font-semibold ${
                    item.changeType === 'increase'
                      ? 'text-green-600'
                      : item.changeType === 'decrease'
                      ? 'text-red-600'
                      : 'text-gray-500'
                  }`}
                >
                  {item.change}
                </p>
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900">
            Derniers adhérents ajoutés
          </h2>
          {loadingAdherents ? (
            <div className="animate-pulse space-y-3 mt-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : recentAdherents && recentAdherents.length > 0 ? (
            <ul className="mt-3 divide-y divide-gray-200">
              {recentAdherents.map((adherent) => {
                // Get the appropriate display name
                const displayName = 
                  (adherent.bigData?.uniteLegale?.prenom1UniteLegale && adherent.bigData?.uniteLegale?.nomUniteLegale) 
                    ? `${adherent.bigData.uniteLegale.prenom1UniteLegale} ${adherent.bigData.uniteLegale.nomUniteLegale}` 
                    : adherent.bigData?.uniteLegale?.denominationUniteLegale || 
                      adherent.bigData?.etablissement?.denominationUsuelleEtablissement || 
                      'Entreprise';
                
                return (
                  <li key={adherent.id} className="py-3">
                    <Link 
                      to={`/adherents/${adherent.id}`}
                      className="block hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {displayName}
                          </p>
                          <p className="text-sm text-gray-500">SIRET: {adherent?.siret}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            Ajouté le {adherent?.createdAt ? new Date(adherent.createdAt).toLocaleDateString('fr-FR') : '-'}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              Aucun adhérent dans votre entreprise. <Link to="/adherents/import" className="text-primary-600 hover:text-primary-700">Importer des adhérents</Link>
            </p>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-gray-900">
            Répartition par département
          </h2>
          {loadingDistributions ? (
            <div className="animate-pulse space-y-3 mt-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : distributions?.departments && distributions.departments.length > 0 ? (
            <ul className="mt-3 space-y-3">
              {distributions.departments.slice(0, 5).map((dept) => (
                <li key={dept.code} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">
                      {dept.code}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({dept.count} adhérents)
                    </span>
                  </div>
                  <div className="w-1/2 bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-primary-600 h-2.5 rounded-full" 
                      style={{ 
                        width: `${Math.min(100, (dept.count / (distributions.departments[0]?.count || 1)) * 100)}%` 
                      }}
                    ></div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-sm text-gray-500">
              Aucune donnée disponible sur la répartition par département.
            </p>
          )}
        </div>
      </div>

      {/* Adherents Map - Moved to the bottom of the page */}
      <div className="card">
        <div className="flex items-center mb-4">
          <MapIcon className="h-5 w-5 text-primary-600 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">
            Carte des adhérents
          </h2>
        </div>
        
        {loadingMapAdherents && !mapAdherents.length ? (
          <div className="h-20 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              <h3 className="ml-3 text-sm font-medium text-red-800">
                Erreur lors du chargement de la carte
              </h3>
            </div>
          </div>
        ) : (
          <AdherentMapComponent 
            adherents={mapAdherents} 
            isLoading={loadingMapAdherents} 
          />
        )}
      </div>
    </div>
  );
} 